import React, { useState, useEffect, useContext } from 'react';
import { Carousel } from 'react-responsive-carousel';
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Button, Typography, Box } from '@mui/material';
import '../styles/css/WelcomeSlideshow.css';
import logoImage from '../styles/images/logo.png';
import { LanguageContext } from '../App';

// Dynamically import images from both orientation folders
const importAll = (r) => {
  try {
    return r.keys().map(r);
  } catch (err) {
    console.error("Error importing images:", err);
    return [];
  }
};

// Wrap in try-catch to prevent crashes if images can't be loaded
let portraitImages = [];
let landscapeImages = [];

const shuffleArray = (array) => {
  const arr = array.slice();
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

try {
  portraitImages = shuffleArray(importAll(require.context('../styles/images/slideshow/portrait', false, /\.webp$/)));
  landscapeImages = shuffleArray(importAll(require.context('../styles/images/slideshow/landscape', false, /\.webp$/)));
} catch (error) {
  console.error("Failed to load slideshow images:", error);
}

const WelcomeSlideshow = () => {
  const [mounted, setMounted] = useState(false);
  const [isPortrait, setIsPortrait] = useState(false);
  const [isRtl, setIsRtl] = useState(false);
  const { language, translations } = useContext(LanguageContext);
  
  useEffect(() => {
    setMounted(true);
    
    const checkOrientation = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
    };
    
    const checkRtl = () => {
      const htmlDir = document.documentElement.getAttribute('dir');
      setIsRtl(htmlDir === 'rtl');
    };
    
    checkOrientation();
    checkRtl();
    
    window.addEventListener('resize', checkOrientation);
    
    return () => window.removeEventListener('resize', checkOrientation);
  }, []);

  // Select images based on orientation
  const images = isPortrait ? portraitImages : landscapeImages;

  const handleScrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleScrollToPortfolio = () => {
    document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Simple loader for initial render
  if (!mounted) {
    return <div className="slideshow-loading">Loading slideshow...</div>;
  }

  return (
    <div className="slideshow-container" dir={isRtl ? 'rtl' : 'ltr'}>
      {/* Static content overlay */}
      <div className="slide-content">
        <img 
          src={logoImage}
          alt="Logo" 
          className="slide-logo" 
        />
        <Typography variant="h2" component="h1" className="slide-title">
          {translations[language].welcomeTitle}
        </Typography>
        <Typography variant="h5" className="slide-description">
          {translations[language].welcomeSubtitle}
        </Typography>
        <Box mt={4}>
          <Button 
            variant="contained" 
            color="primary" 
            size="large"
            onClick={handleScrollToContact}
          >
            {translations[language].contactBtn}
          </Button>
          <Button 
            variant="outlined" 
            size="large"
            sx={{ 
              ml: isRtl ? 0 : 2, 
              mr: isRtl ? 2 : 0, 
              color: 'white', 
              borderColor: 'white' 
            }}
            onClick={handleScrollToPortfolio}
          >
            {translations[language].portfolioBtn}
          </Button>
        </Box>
      </div>
      
      {/* Simplified carousel */}
      {images.length > 0 ? (
        <div className="carousel-container">
          <Carousel 
            showThumbs={false}
            showStatus={false}
            infiniteLoop={true}
            autoPlay={true}
            interval={4000}
            transitionTime={1000}
            animationHandler="fade"
            showArrows={true}
            showIndicators={true}
            swipeable={false}
            emulateTouch={false}
            stopOnHover={false}
            className="image-carousel"
          >
            {images.map((image, index) => (
              <div key={index} className="carousel-slide">                
                <img 
                  src={image} 
                  alt={`Slide ${index + 1}`}
                  className="carousel-image"
                />
              </div>
            ))}
          </Carousel>
        </div>
      ) : (
        <div className="no-images-fallback">
          <Typography variant="h6" color="error">
            No slideshow images available
          </Typography>
        </div>
      )}
    </div>
  );
};

export default WelcomeSlideshow;
